import React from "react";
import { motion } from "framer-motion";
import BuildingIllustration from "./BuildingIllustration";

const domains = ["Fintech", "Marketplace", "Identity Verification"];

const stats = [
  { value: "6+", label: "Products shipped" },
  { value: "3", label: "Regions served" },
  { value: "3", label: "Industries" },
];

const CodehouzeHero = ({ onStartProject }) => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-codehouze-hero-gradient"
    >
      {/* decorative grid */}
      <div
        className="absolute inset-0 bg-codehouze-grid bg-grid opacity-60"
        style={{ maskImage: "radial-gradient(ellipse at center, black 0%, transparent 70%)" }}
      />

      {/* floating gradient orbs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-codehouzeMaroon/30 rounded-full blur-3xl animate-blob" />
      <div className="absolute top-1/3 -right-24 w-96 h-96 bg-codehouzeOrange/20 rounded-full blur-3xl animate-blob [animation-delay:4s]" />

      <div className="relative mx-auto max-w-6xl w-full px-6 py-20 flex flex-col md:flex-row items-center gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex-1 flex flex-col items-center md:items-start text-center md:text-left"
        >
          <div className="inline-flex items-center gap-2 bg-codehouzeOrange/10 border border-codehouzeOrange/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-codehouzeOrange animate-pulse" />
            <span className="text-codehouzeOrange text-sm font-medium">
              Open for new projects
            </span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold text-white mb-4 leading-tight tracking-tight">
            Engineering software
            <br />
            <span className="codehouze-text-gradient">that performs.</span>
          </h1>

          <p className="text-gray-400 text-lg mb-4 max-w-lg leading-relaxed">
            Codehouze Technology Solutions is a full-stack development and consulting
            studio. We design and build web and mobile products end-to-end, from
            REST APIs and microservices to production-grade frontends.
          </p>

          <p className="text-gray-500 text-base mb-6 max-w-lg leading-relaxed">
            We've delivered fintech, marketplace, and identity-verification
            platforms for clients across Nigeria, Canada, and Europe. Every engagement,
            from early-stage MVP to systems running at scale, gets the same
            engineering rigor.
          </p>

          <div className="flex gap-4 flex-wrap justify-center md:justify-start mb-8">
            <button
              type="button"
              onClick={onStartProject}
              className="inline-flex text-white bg-gradient-to-r from-codehouzeMaroon to-codehouzeOrange hover:brightness-110 border-0 py-2.5 px-6 focus:outline-none rounded-full text-sm font-medium transition-all shadow-lg shadow-codehouzeOrange/20"
            >
              Start a Project
            </button>
            <a
              href="#work"
              className="inline-flex text-gray-300 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white py-2.5 px-6 focus:outline-none rounded-full text-sm font-medium transition-colors"
            >
              See Our Work
            </a>
          </div>

          <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-8">
            {domains.map((domain) => (
              <span
                key={domain}
                className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-full px-3 py-1"
              >
                {domain}
              </span>
            ))}
          </div>

          <div className="flex gap-8 justify-center md:justify-start">
            {stats.map(({ value, label }) => (
              <div key={label} className="text-center md:text-left">
                <p className="codehouze-text-gradient text-2xl font-bold">{value}</p>
                <p className="text-gray-500 text-xs mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
          className="flex-shrink-0 relative w-full max-w-xs sm:max-w-md"
        >
          <div className="absolute inset-0 bg-codehouzeOrange/20 rounded-full blur-3xl scale-75" />
          <BuildingIllustration className="relative w-full h-auto animate-float" />
        </motion.div>
      </div>
    </section>
  );
};

export default CodehouzeHero;
